import { api } from './client'
import type {
    Feedback,
    FeedbackSearchParams,
    FeedbackSummary,
    ApiEnvelope,
    PaginatedResponse,
    SatisfactionScore,
    ProblemType,
} from '@/types/api'

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface RobotFeedback {
    id: string
    satisfactionScore: number
    satisfactionLevel: string
    createdAt: string
    robotName: string
    unitName: string
    satisfactionContent: string
}

export interface RobotFeedbackSearchParams {
    satisfactionScore?: number[]
    fromDate?: string
    toDate?: string
    robotName?: string
    robotId?: string
    unitId?: string
}

function adaptPage<T>(
    raw: SpringPage<T> | PaginatedResponse<T>,
): PaginatedResponse<T> {
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements,
        page: (raw.number) + 1,
        limit: raw.size,
        totalPages: raw.totalPages,
    }
}

export async function fetchFeedbacks(
    page: number = 0,
    size: number = 10,
    filters: Partial<FeedbackSearchParams> = {},
    signal?: AbortSignal,
): Promise<PaginatedResponse<Feedback>> {
    try {
        const { satisfactionScore, ...rest } = filters

        // Support both single value and array payload for backend
        const params = {
            page,
            size,
            ...rest,
            ...(satisfactionScore !== undefined
                ? { satisfactionScore }
                : {}),
        }

        const response = await api.get<ApiEnvelope<SpringPage<Feedback>>>(
            '/v1/survey/search',
            { params, signal },
        )
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách đánh giá.';
        throw new Error(message);
    }
}

export async function fetchRobotFeedbacks(
    page: number = 0,
    size: number = 10,
    filters: RobotFeedbackSearchParams = {},
    signal?: AbortSignal,
): Promise<PaginatedResponse<RobotFeedback>> {
    try {
        const { satisfactionScore, ...rest } = filters
        const params = {
            page,
            size,
            ...rest,
            ...(satisfactionScore !== undefined ? { satisfactionScore } : {}),
        }

        const response = await api.get<ApiEnvelope<SpringPage<RobotFeedback>>>(
            '/v1/survey/robot/search',
            { params, signal },
        )

        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message =
            error?.response?.data?.responseMessage ||
            error?.message ||
            'Đã xảy ra lỗi khi lấy danh sách đánh giá robot.'
        throw new Error(message)
    }
}

export async function fetchSatisfactionScores(signal?: AbortSignal): Promise<SatisfactionScore[]> {
    try {
        const response = await api.get<ApiEnvelope<SatisfactionScore[]>>(
            '/v1/survey/satisfaction-score',
            { signal },
        )

        const data = response.data.responseData;
        return data.filter(item => [2, 3, 4].includes(item.score));

    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách mức độ hài lòng.';
        throw new Error(message);
    }
}

/**
 * Submit new feedback (Đánh giá hài lòng).
 */
export async function createFeedback(data: {
    documentId: string
    surveyTypeName: string
    surveySubTypeName: string
    satisfactionScore: number
    customerImage?: string
}): Promise<void> {
    try {
        await api.post('/v1/survey/save', data)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi gửi đánh giá.';
        throw new Error(message);
    }
}

export async function fetchFeedbackSummary(
    scores: SatisfactionScore[],
    filters: Partial<FeedbackSearchParams> = {},
    signal?: AbortSignal,
): Promise<FeedbackSummary> {
    try {
        const { satisfactionScore, ...restFilters } = filters
        const response = await api.get<ApiEnvelope<any[]>>(
            '/v1/survey/stat',
            { params: restFilters, signal }
        )

        const statData = response.data.responseData

        const targetScores = [2, 3, 4];

        return {
            counts: scores
                .filter((s) => targetScores.includes(s.score))
                .map((s) => {
                    const found = statData.find(
                        (item: any) => item.score?.score === s.score || item.score === s.score,
                    )
                    return {
                        score: s.score,
                        name: s.description,
                        count: found ? found.total : 0,
                    }
                }),
        }
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy thống kê đánh giá.';
        throw new Error(message);
    }
}

export async function fetchRobotFeedbackSummary(
    scores: SatisfactionScore[],
    filters: RobotFeedbackSearchParams = {},
    signal?: AbortSignal,
): Promise<FeedbackSummary> {
    try {
        const response = await api.get<ApiEnvelope<any[]>>(
            '/v1/survey/robot/stat',
            { params: filters, signal },
        )

        const responseData = response.data.responseData
        const statData = Array.isArray(responseData) ? responseData : []
        const targetScores = [2, 3, 4]

        return {
            counts: scores
                .filter((score) => targetScores.includes(score.score))
                .map((score) => {
                    const found = statData.find(
                        (item: any) =>
                            item.score?.score === score.score ||
                            item.score === score.score,
                    )

                    return {
                        score: score.score,
                        name: score.description,
                        count: Number(found?.total ?? 0),
                    }
                }),
        }
    } catch (error: any) {
        const message =
            error?.response?.data?.responseMessage ||
            error?.message ||
            'Đã xảy ra lỗi khi lấy thống kê đánh giá robot.'
        throw new Error(message)
    }
}

export async function fetchProblemTypes(signal?: AbortSignal): Promise<ProblemType[]> {
    try {
        const response = await api.get<ApiEnvelope<ProblemType[]>>(
            '/v1/survey/problem-type',
            { signal },
        )

        const data = response.data.responseData
        if (!Array.isArray(data)) return []

        return data.filter((item) => !!item.id && !!item.name)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách loại phản ánh.';
        throw new Error(message);
    }
}